import { z } from 'zod';

// Contracts of the Comms module (requests, messages, deliveries), shared with the web app and agent tools.

export const Id = z.uuid();
export const RequestCategory = z.enum(['maintenance', 'question', 'renovation', 'document', 'complaint', 'other']);
export const RequestStatus = z.enum(['new', 'in_progress', 'waiting', 'resolved']);
export const RequestPriority = z.enum(['normal', 'urgent']);

export const CreateRequest = z.object({
  category: RequestCategory,
  priority: RequestPriority.default('normal'),
  subject: z.string().trim().min(3).max(200),
  description: z.string().trim().min(1).max(10_000),
  unitId: Id.optional(),
  /** Board only: log a request received by phone or email on behalf of a co-owner. */
  requesterPartyId: Id.optional(),
});
export type CreateRequest = z.infer<typeof CreateRequest>;

export const PostMessage = z.object({
  body: z.string().trim().min(1).max(10_000),
  /** Board only: a note visible to the board, never to the requester, never emailed. */
  internal: z.boolean().default(false),
});
export type PostMessage = z.infer<typeof PostMessage>;

export const UpdateRequest = z
  .object({
    status: RequestStatus.optional(),
    priority: RequestPriority.optional(),
    category: RequestCategory.optional(),
    assigneeUserId: Id.nullable().optional(),
  })
  .refine((u) => Object.values(u).some((v) => v !== undefined), 'Nothing to update');
export type UpdateRequest = z.infer<typeof UpdateRequest>;

export const RequestsQuery = z.object({ status: z.enum(['open', 'all', ...RequestStatus.options]).default('open') });

export interface RequestSummary {
  id: string;
  number: number;
  subject: string;
  category: z.infer<typeof RequestCategory>;
  priority: z.infer<typeof RequestPriority>;
  status: z.infer<typeof RequestStatus>;
  unitId: string | null;
  requesterPartyId: string | null;
  requesterUserId: string | null;
  assigneeUserId: string | null;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

export interface Message {
  id: string;
  authorUserId: string | null;
  authorPartyId: string | null;
  /** Display name of the author, resolved for the reader. */
  authorName: string | null;
  visibility: 'public' | 'internal';
  body: string;
  createdAt: string;
  /** Emails sent for this message, with their status (proof of delivery). */
  deliveries: { address: string; status: Delivery['status']; sentAt: string | null }[];
  /** Files attached to the message (photos, PDFs). */
  attachments: Attachment[];
}

export interface Attachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

/** Files a request message may carry: images and PDFs, 10 MB each, 5 per message. */
export const ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif', 'application/pdf'];
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const ATTACHMENT_MAX_COUNT = 5;

export interface RequestDetail extends RequestSummary {
  messages: Message[];
}

export interface Delivery {
  id: string;
  address: string;
  subject: string;
  bodyText: string;
  language: 'fr' | 'en';
  status: 'queued' | 'sending' | 'sent' | 'failed';
  provider: string | null;
  attempts: number;
  lastError: string | null;
  queuedAt: string;
  sentAt: string | null;
}
